import React, { Suspense, useEffect, useState } from "react";

import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Stack,
} from "@chakra-ui/react";
import {
  ChevronDown,
  ChevronUp,
  IoClose,
  MenuFill,
  VellisLogoIcon,
  VellisLogoSmallIcon,
} from "../../customIcons";
import { solutionsLinks } from "../../../constants/solutionsLinks"; // Import the solutionsLinks array

const CustomMobileDrawer = React.lazy(
  () => import("../../molecules/CustomMobileDrawer")
);

type HeaderProps = {
  alwaysVisible?: boolean;
};

const Header = ({ alwaysVisible }: HeaderProps) => {
  const [scrollingDown, setScrollingDown] = useState(false);
  const [prevScrollY, setPrevScrollY] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > prevScrollY && prevScrollY > 0) {
        setScrollingDown(true);
      } else {
        setScrollingDown(false);
      }

      setPrevScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollY]);

  return (
    <Box
      style={{
        position: "fixed",
        top: alwaysVisible ? 0 : scrollingDown ? "-112px" : "0",
        transition: "top 0.3s ease-in-out",
        width: "100%",
        backgroundColor: "#fff",
        zIndex: 1000,
      }}
      w="100%"
      as="section"
    >
      <HStack
        py={{ base: 3, lg: 5 }}
        gap={0}
        maxW="1320px"
        mx="auto"
        w="100%"
        justifyContent="space-between"
        px={{ base: 4, "2xl": 0 }}
      >
        <a href="/">
          <VellisLogoIcon
            display={{ base: "none", xl: "flex" }}
            w="160px"
            h="36px"
          />

          <VellisLogoSmallIcon
            display={{ base: "flex", xl: "none" }}
            w="64px"
            h="45px"
          />
        </a>
        <HStack
          display={{ base: "none", xl: "flex" }}
          gap={0}
          w="1000px"
          h="48px"
          className="medium-text"
          lineHeight="150%"
          color="#1B1E1E"
          justifyContent="space-between"
        >
          <Menu>
            {({ isOpen }) => (
              <Stack>
                <HStack
                  gap={0}
                  h="48px"
                  w="100%"
                  bg="transparent"
                  className="medium-text"
                  _hover={{
                    color: "blackAlpha.600",
                    ".icon": {
                      fill: "blackAlpha.600",
                    },
                  }}
                >
                  <a
                    href="/financial-services"
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    Financial Services
                  </a>
                  <MenuButton as={Button} variant="unstyled" padding={0}>
                    <Box
                      mt={-1}
                      as={isOpen ? ChevronUp : ChevronDown}
                      color="currentColor"
                      className="icon"
                    />
                  </MenuButton>
                </HStack>

                <MenuList
                  mt={-1}
                  boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)"
                  border="1px solid #E1E7EF"
                  w="280px"
                  p={0}
                  borderRadius="8px"
                  color="#4A5568"
                  className="regular-text"
                  fontSize="14px"
                  position="absolute"
                  right={-20}
                >
                  <a href={"/financial-services/multi-currency-accounts"}>
                    <MenuItem borderTopRadius="8px" h="40px">
                      Multi-Currency Accounts
                    </MenuItem>
                  </a>
                  <a href={"/financial-services/business-payments"}>
                    <MenuItem h="40px">Payments</MenuItem>
                  </a>
                  <a href={"/financial-services/foreign-exchange"}>
                    <MenuItem h="40px">Foreign Exchange</MenuItem>
                  </a>
                  <a href={"/financial-services/payment-processing"}>
                    <MenuItem borderBottomRadius="8px" h="40px">
                      Payment Processing
                    </MenuItem>
                  </a>
                </MenuList>
              </Stack>
            )}
          </Menu>
          <Menu>
            {({ isOpen }) => (
              <Stack>
                <HStack
                  gap={0}
                  px={3}
                  h="48px"
                  w="100px"
                  bg="transparent"
                  className="medium-text"
                  _hover={{
                    color: "blackAlpha.600",
                    ".icon": {
                      fill: "blackAlpha.600",
                    },
                  }}
                >
                  <a
                    href="/solutions"
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    Solutions
                  </a>

                  <MenuButton as={Button} variant="unstyled" padding={0}>
                    <Box
                      mt={-1}
                      as={isOpen ? ChevronUp : ChevronDown}
                      color="currentColor"
                      className="icon"
                    />
                  </MenuButton>
                </HStack>

                <MenuList
                  mt={-1}
                  boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)"
                  border="1px solid #E1E7EF"
                  w="280px"
                  p={0}
                  borderRadius="8px"
                  color="#4A5568"
                  className="regular-text"
                  fontSize="14px"
                  position="absolute"
                  right={-40}
                >
                  {solutionsLinks.map(({ title, link }) => (
                    <a key={link} href={link}>
                      <MenuItem h="40px">{title}</MenuItem>
                    </a>
                  ))}
                </MenuList>
              </Stack>
            )}
          </Menu>
          <Menu>
            {({ isOpen }) => (
              <>
                <MenuButton
                  ml={4}
                  as={Button}
                  h="48px"
                  px={3}
                  className="medium-text"
                  w="140px"
                  bg="transparent"
                  _hover={{
                    color: "blackAlpha.600",
                    ".icon": { fill: "blackAlpha.600" },
                    bg: "transparent",
                  }}
                  _expanded={{
                    bg: "transparent",
                  }}
                  lineHeight="150%"
                  rightIcon={
                    <Box
                      fill="black"
                      as={isOpen ? ChevronUp : ChevronDown}
                      className="icon"
                    />
                  }
                >
                  Get Started
                </MenuButton>
                <MenuList
                  mt={-2}
                  boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)"
                  border="1px solid #E1E7EF"
                  w="280px"
                  p={0}
                  borderRadius="8px"
                  color="#4A5568"
                  className="regular-text"
                  fontSize="14px"
                >
                  <a href="/how-it-works">
                    <MenuItem borderTopRadius="8px" h="40px">
                      How it Works
                    </MenuItem>
                  </a>
                  <a href="/plans">
                    <MenuItem h="40px">Plans</MenuItem>
                  </a>
                  <a href="/partners">
                    <MenuItem h="40px">Partners</MenuItem>
                  </a>
                  <a href="/supported-currencies">
                    <MenuItem h="40px">Supported Currencies</MenuItem>
                  </a>
                  <a href="/faq">
                    <MenuItem h="40px">FAQ</MenuItem>
                  </a>
                  <MenuItem
                    borderBottomRadius="8px"
                    h="40px"
                    as="a"
                    href="/contact-us"
                  >
                    Contact Us
                  </MenuItem>
                </MenuList>
              </>
            )}
          </Menu>

          <Menu>
            {({ isOpen }) => (
              <>
                <MenuButton
                  as={Button}
                  h="48px"
                  px={1}
                  mr={2}
                  className="medium-text"
                  w="120px"
                  bg="transparent"
                  _hover={{
                    color: "blackAlpha.600",
                    ".icon": { fill: "blackAlpha.600" },
                    bg: "transparent",
                  }}
                  _expanded={{
                    bg: "transparent",
                  }}
                  lineHeight="150%"
                  rightIcon={
                    <Box
                      fill="black"
                      as={isOpen ? ChevronUp : ChevronDown}
                      className="icon"
                    />
                  }
                >
                  Resources
                </MenuButton>
                <MenuList
                  mt={-2}
                  boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)"
                  border="1px solid #E1E7EF"
                  w="280px"
                  p={0}
                  borderRadius="8px"
                  color="#4A5568"
                  className="regular-text"
                  fontSize="14px"
                >
                  <a href="/about-us">
                    <MenuItem borderTopRadius="8px" h="40px">
                      About Us
                    </MenuItem>
                  </a>
                  <a href="/help-centre">
                    <MenuItem borderTopRadius="8px" h="40px">
                      Help Centre
                    </MenuItem>
                  </a>
                  <a href="/contact-support">
                    <MenuItem h="40px">Contact Support</MenuItem>
                  </a>
                  <a href="/security">
                    <MenuItem borderBottomRadius="8px" h="40px">
                      Security
                    </MenuItem>
                  </a>
                  <a href="/blog">
                    <MenuItem borderBottomRadius="8px" h="40px">
                      Blog
                    </MenuItem>
                  </a>
                </MenuList>
              </>
            )}
          </Menu>
          {/* <Button
            h="48px"
            bg="transparent"
            _hover={{ color: "#4C9195" }}
            color="#116266"
            borderRadius="9999px"
            w={{ base: "70px", xl: "90px" }}
            className="medium-text"
            as="a"
            href="/contact-us"
          >
            Log in
          </Button> */}
          <Button
            h="48px"
            color="#fff"
            _hover={{ bg: "#4C9195" }}
            bg="#116266"
            borderRadius="9999px"
            // w={{ base: "100px", xl: "130px" }}
            className="medium-text"
            as="a"
            href="/contact-us"
          >
            Contact Us
          </Button>
        </HStack>
        <Box display={{ base: "flex", xl: "none" }}>
          <Box
            alignItems="center"
            justifyContent="center"
            bg="#116266"
            borderRadius="9999px"
            w="48px"
            h="48px"
            onClick={toggleDrawer}
          >
            <IconButton
              aria-label=""
              h="100%"
              w="100%"
              color="#fff"
              variant="unstyled"
              display="inline-flex"
              icon={
                isDropdownOpen ? (
                  <IoClose width="20px" height="20px" />
                ) : (
                  <MenuFill style={{ width: "20px", height: "20px" }} />
                )
              }
            />
          </Box>
          <Suspense>
            <CustomMobileDrawer
              isOpen={isDropdownOpen}
              onClose={toggleDrawer}
            />
          </Suspense>
        </Box>
      </HStack>
    </Box>
  );
};

export default Header;
